import { useMemo } from 'react';
/* The redesigned eVault (spec 25). The page it replaces —
   `views/evault/evault-page.tsx`, which mounts esign.frontend's
   <simplici-evault> web component — is deliberately NOT deleted: the file, its
   scss and the component host stay on disk and exported, simply no longer
   routed, exactly as `sessions.tsx` and `simplici-sign-dashboard/` were kept
   when their decks landed. Re-import it here to put the old page back.
   import { EVaultPage } from 'views/evault'; */
import { EvaultDeck, TransfersDeck, VaultDeck } from 'views/evault/deck';
import {
	CountryCoverageDeck,
	HistoricalDeck,
	InvestigateDeck,
	RulesDeck,
	TriageDeck,
} from 'views/transaction-monitoring';
import { DataPullsDeck } from 'views/data-pulls';

import DashboardPage from 'views/dashboard-analytics/dashboard-page';
import { ROUTES } from './constants';
// Onboarding → Onboarding Flow, redesigned (spec: _design/specs/01-onboarding-flow.md).
// The screen it replaces is still in git at views/pipeline/pipeline.tsx.
import { OnboardingDeck } from 'views/pipelines/deck';
import OnboardingFlow from 'views/onboarding-flow/onboarding-flow';
// The redesigned surfaces. Sessions and eSign now render the v2 design
// (see `_design/simplici-v2-design.html`); the screens they replace are still
// in git at views/sessions/sessions.tsx and views/simplici-sign-dashboard.
import { SessionsDeck } from 'views/sessions/deck';
// The sv2 funds list. The screen it replaces is untouched in git
// (views/funds/funds.tsx) and still imported by nothing else — same pattern as
// sessions/sessions.tsx when the Sessions deck landed.
import FundsDeck from 'views/funds/deck/funds-deck';
import FundDetails from 'views/funds-pages/funds-Details';
import { EsignDeck } from 'views/esign-deck';
import { EsignTemplatesDeck } from 'views/esign-templates-deck';
import { EsignInboxDeck } from 'views/esign-inbox-deck';
import { EsignSentDeck } from 'views/esign-sent-deck';
import { EsignDraftDeck } from 'views/esign-draft-deck';
import { UserRolesDeck } from 'views/user-roles/deck';
import Integrate from 'views/integrate/integrate';
import Document from 'views/document/document';
import Analytics from 'views/analytics/analytics';
import SupportAgent from 'views/support-agent/support-agent';
/* The redesigned billing surface (spec 18). The previous page —
   `views/settings/billing/billing` — is deliberately NOT deleted: the file and
   all eight of its sections stay on disk and exported, simply no longer routed,
   exactly as `sessions.tsx` and `simplici-sign-dashboard/` were kept when their
   decks landed. Re-import it here to put the old page back. */
import { BillingDeck } from 'views/settings/billing/deck';
/* The four Settings surfaces, same pattern: the pages they replace stay on
   disk and exported at views/credential/credential.tsx,
   views/sub-account/sub-account.tsx, views/settings/settings.tsx and
   views/user-roles/user-roles.tsx — only the route element changed. */
import { DeveloperKeysDeck } from 'views/credential/deck';
import { SubAccountsDeck } from 'views/sub-account/deck';
import { GeneralSettingsDeck } from 'views/settings/deck';

const {
	CREDENTIALS,
	SETTINGS,
	USER_ROLES,
	SESSIONS,
	FUNDS,
	SIGNED_DOC,
	SIGNED_DOC_INBOX,
	BILLING,
	INTEGRATE,
	DASHBOARD,
	API,
	ANALYTICS,
	SUPPORT_AGENT,
	SIMPLICI_SIGN_DASHBOARD,
	INBOX,
	PIPELINE,
	FLOW,
	SUB_ACCOUNT,
	ESIGN_DRAFT,
	EVAULT,
	EVAULT_DETAIL,
	EVAULT_TRANSFERS,
	TRANSACTION_MONITORING,
	TM_INVESTIGATE,
	TM_RULES,
	TM_COUNTRY_COVERAGE,
	DATA_PULLS,
	TM_HISTORICAL,
} = ROUTES;

interface IRoutesModuleProps {
	fundsLoaded?: any;
	settingsLoaded?: any;
	IsCredentials?: boolean;
	userRoleLoading?: any;
	userRolesLoaded?: any;
	settingsLoading?: any;
}

export const useRoutesModule = ({
	fundsLoaded,
	IsCredentials,
}: IRoutesModuleProps) => {
	const ALL_ROUTES_MODULE = useMemo(() => {
		const ALL_ROUTES_MODULE = {
			dashboard: {
				path: DASHBOARD,
				element: <DashboardPage />,
			},
			onboarding: {
				path: PIPELINE,
				children: [
					{
						element: <OnboardingDeck />,
						index: true,
					},
					{
						path: `${PIPELINE}${FLOW}`,
						element: <OnboardingFlow />,
					},
				],
			},
			session: {
				path: SESSIONS,
				element: <SessionsDeck />,
			},
			funds: {
				path: FUNDS,
				children: [
					{
						element: <FundsDeck loaded={fundsLoaded} />,
						index: true,
					},
					{
						path: `${FUNDS}/:id`,
						element: <FundDetails />,
					},
				],
			},
			signDashboard: {
				path: SIMPLICI_SIGN_DASHBOARD,
				element: <EsignDeck />,
			},
			template: {
				path: SIGNED_DOC,
				element: <EsignTemplatesDeck />,
			},
			inbox: {
				path: INBOX,
				element: <EsignInboxDeck />,
			},
			sent: {
				path: SIGNED_DOC_INBOX,
				element: <EsignSentDeck />,
			},
			generalSettings: {
				path: SETTINGS,
				element: <GeneralSettingsDeck />,
			},
			developersKeys: {
				path: CREDENTIALS,
				element: <DeveloperKeysDeck loading={IsCredentials} />,
			},
			userRoles: {
				path: USER_ROLES,
				element: <UserRolesDeck />,
			},
			subAccount: {
				path: SUB_ACCOUNT,
				element: <SubAccountsDeck />,
			},
			embedWeb: {
				path: INTEGRATE,
				element: <Integrate />,
			},
			apiDocs: {
				path: API,
				element: <Document />,
			},
			userAnalytics: {
				path: ANALYTICS,
				element: <Analytics />,
			},
			supportAgent: {
				path: SUPPORT_AGENT,
				element: <SupportAgent />,
			},
			billing: {
				path: BILLING,
				element: <BillingDeck />,
			},
			// INTERNAL ONLY. Top level rather than under transaction-monitoring: the
			// pulls it reports on belong to several services, not one product.
			dataPulls: {
				path: DATA_PULLS,
				element: <DataPullsDeck />,
			},
			draft: {
                path: ESIGN_DRAFT,
                element: <EsignDraftDeck />,
            },
			// Key must equal the permission key: getCustomRoutes looks each route up
			// by the names in customUserTab, which come straight from role.permissions.
			// The drive, the transfers list and one vault are CHILDREN of the same
			// entry — the `funds` / `funds/:id` shape — so all three surfaces
			// answer to the one `evault` permission rather than needing a second
			// key. That is also why SUB_SIDEBAR maps `evault` to a LIST of two
			// labels: the sidebar's two rows have only this one key between them.
			// Two SEPARATE permission keys, unlike evault's parent/child pair
			// below: triage and investigate are different jobs (working the
			// queue vs. building a case), and a role that reviews alerts is not
			// automatically one that should browse every subject's history.
			transactionMonitoring: {
				path: TRANSACTION_MONITORING,
				element: <TriageDeck />,
			},
			tmInvestigate: {
				path: TM_INVESTIGATE,
				element: <InvestigateDeck />,
			},
			// A THIRD permission key, for the same reason there are two above:
			// retuning the rules changes what the engine scores for everyone in
			// the business, which is a different job from working the queue those
			// rules fill. Registering the route is not enough on its own -- the
			// key also has to exist on clients.api's roles model AND in
			// seed-all.js's PERMISSION_KEYS, or the row silently never renders.
			tmRules: {
				path: TM_RULES,
				element: <RulesDeck />,
			},
			// A FOURTH key. Bulk-loading a client's whole ledger is a materially
			// different capability from working alerts, browsing a subject, or
			// retuning the rules -- and the one most worth withholding from an
			// analyst who does none of those. Same seven registration points as
			// tmRules; see the group in routes/constants.tsx.
			tmHistorical: {
				path: TM_HISTORICAL,
				element: <HistoricalDeck />,
			},
			// A FIFTH key, for the same reason as the fourth. Country coverage is
			// the only screen on which a single click DECLINES every future
			// transaction naming a country -- a blunter instrument than retuning a
			// rule's score, and one an analyst working alerts has no need of.
			tmCountryCoverage: {
				path: TM_COUNTRY_COVERAGE,
				element: <CountryCoverageDeck />,
			},

			evault: {
				path: EVAULT,
				children: [
					{
						index: true,
						element: <EvaultDeck />,
					},
					/* BEFORE the id route, not after. React Router ranks a static
					   segment above a dynamic one, so ordering is not what makes
					   this work — it is what makes it obvious. `/evault/:id`
					   would otherwise read `transfers` as a vault id and render
					   "This vault isn't here". */
					{
						path: EVAULT_TRANSFERS,
						element: <TransfersDeck />,
					},
					{
						path: EVAULT_DETAIL,
						element: <VaultDeck />,
					},
				],
			},
		};
		return ALL_ROUTES_MODULE;
	}, [IsCredentials, fundsLoaded]);

	return { ALL_ROUTES_MODULE };
};

// Awadhesh: lazy loading

// import { lazy, useMemo } from 'react';
// import { ROUTES } from './constants';

// const {
// 	CREDENTIALS,
// 	SETTINGS,
// 	USER_ROLES,
// 	SESSIONS,
// 	FUNDS,
// 	SIGNED_DOC,
// 	SIGNED_DOC_INBOX,
// 	BILLING,
// 	INTEGRATE,
// 	TRANSACTIONS,
// 	DASHBOARD,
// 	API,
// 	ANALYTICS,
// 	SUPPORT_AGENT,
// 	SIMPLICI_SIGN_DASHBOARD,
// 	INBOX,
// 	PIPELINE,
// 	FLOW,
// 	CHEQUE_FRAUD,
// 	SUB_ACCOUNT,
// } = ROUTES;

// interface IRoutesModuleProps {
// 	fundsLoaded?: any;
// 	credentialsLoading?: any;
// 	credentialsLoaded?: any;
// }

// const retry = (
// 	fn: () => Promise<unknown>,
// 	retriesLeft = 5,
// 	interval = 1000
// ) => {
// 	return new Promise((resolve, reject) => {
// 		fn()
// 			.then(resolve)
// 			.catch(error => {
// 				setTimeout(() => {
// 					if (retriesLeft === 1) {
// 						// reject('maximum retries exceeded');
// 						reject(error);
// 						return;
// 					}
// 					// Passing on "reject" is the important part
// 					retry(fn, retriesLeft - 1, interval).then(resolve, reject);
// 				}, interval);
// 			});
// 	});
// };

// const DashboardPage = lazy(
// 	() =>
// 		retry(() => import(/* webpackChunkName: "dashboard" */'../views/dashboard-analytics/dashboard-page')) as any
// );
// const Pipeline = lazy(
// 	() => retry(() => import(/* webpackChunkName: "pipeline" */'../views/pipeline/pipeline')) as any
// );
// const OnboardingFlow = lazy(
// 	() => retry(() => import(/* webpackChunkName: "onboarding-flow" */'../views/onboarding-flow/onboarding-flow')) as any
// );
// const Sessions = lazy(
// 	() => retry(() => import(/* webpackChunkName: "sessions" */'../views/sessions/sessions')) as any
// );
// const Funds = lazy(() => retry(() => import(/* webpackChunkName: "funds" */'../views/funds/funds')) as any);
// const FundDetails = lazy(
// 	() => retry(() => import(/* webpackChunkName: "funds-Details" */'../views/funds-pages/funds-Details')) as any
// );
// const ESignDashboard = lazy(
// 	() =>
// 		retry(
// 			() => import(/* webpackChunkName: "sign-dashboard" */'../views/simplici-sign-dashboard/sign-dashboard')
// 		) as any
// );
// const SignedDoc = lazy(
// 	() => retry(() => import(/* webpackChunkName: "signed-doc" */'../views/signed-doc/signed-doc')) as any
// );
// const SignedDocPacketsInbox = lazy(
// 	() =>
// 		retry(
// 			() => import(/* webpackChunkName: "signed-doc-packets-inbox" */'../views/signed-doc-packets-inbox/signed-doc-packets-inbox')
// 		) as any
// );
// const SignedDocInbox = lazy(
// 	() => retry(() => import(/* webpackChunkName: "signed-doc-inbox" */'../views/signed-doc-inbox/signed-doc-inbox')) as any
// );
// const Transactions = lazy(
// 	() => retry(() => import(/* webpackChunkName: "transactions" */'../views/transactions/transactions')) as any
// );
// const Settings = lazy(
// 	() => retry(() => import(/* webpackChunkName: "settings" */'../views/settings/settings')) as any
// );
// const Credential = lazy(
// 	() => retry(() => import(/* webpackChunkName: "credential" */'../views/credential/credential')) as any
// );
// const UserRoles = lazy(
// 	() => retry(() => import(/* webpackChunkName: "user-roles" */'../views/user-roles/user-roles')) as any
// );
// const SubAccount = lazy(
// 	() => retry(() => import(/* webpackChunkName: "sub-account" */'../views/sub-account/sub-account')) as any
// );
// const Integrate = lazy(
// 	() => retry(() => import(/* webpackChunkName: "integrate" */'../views/integrate/integrate')) as any
// );
// const Document = lazy(
// 	() => retry(() => import(/* webpackChunkName: "document" */'../views/document/document')) as any
// );
// const Analytics = lazy(
// 	() => retry(() => import(/* webpackChunkName: "analytics" */'../views/analytics/analytics')) as any
// );
// const SupportAgent = lazy(
// 	() => retry(() => import(/* webpackChunkName: "support-agent" */'../views/support-agent/support-agent')) as any
// );
// const Billing = lazy(
// 	() => retry(() => import(/* webpackChunkName: "billing" */'../views/settings/billing/billing')) as any
// );
// const ChequeFraud = lazy(
// 	() => retry(() => import(/* webpackChunkName: "cheque-fraud" */'../views/cheque-fraud/cheque-fraud')) as any
// );

// export const useRoutesModule = ({
// 	fundsLoaded,
// 	credentialsLoading,
// 	credentialsLoaded,
// }: IRoutesModuleProps) => {
// 	const ALL_ROUTES_MODULE = useMemo(() => {
// 		const ALL_ROUTES_MODULE = {
// 			dashboard: {
// 				path: DASHBOARD,
// 				element: <DashboardPage />,
// 			},
// 			onboarding: {
// 				path: PIPELINE,
// 				children: [
// 					{
// 						element: <Pipeline />,
// 						index: true,
// 					},
// 					{
// 						path: `${PIPELINE}${FLOW}`,
// 						element: <OnboardingFlow />,
// 					},
// 				],
// 			},
// 			session: {
// 				path: SESSIONS,
// 				element: <Sessions />,
// 			},
// 			funds: {
// 				path: FUNDS,
// 				children: [
// 					{
// 						element: <FundsDeck loaded={fundsLoaded} />,
// 						index: true,
// 					},
// 					{
// 						path: `${FUNDS}/:id`,
// 						element: <FundDetails />,
// 					},
// 				],
// 			},
// 			signDashboard: {
// 				path: SIMPLICI_SIGN_DASHBOARD,
// 				element: <ESignDashboard />,
// 			},
// 			template: {
// 				path: SIGNED_DOC,
// 				element: <SignedDoc />,
// 			},
// 			inbox: {
// 				path: INBOX,
// 				element: <SignedDocPacketsInbox />,
// 			},
// 			sent: {
// 				path: SIGNED_DOC_INBOX,
// 				element: <SignedDocInbox />,
// 			},
// 			transaction: {
// 				path: TRANSACTIONS,
// 				element: <Transactions />,
// 			},
// 			generalSettings: {
// 				path: SETTINGS,
// 				element: <Settings />,
// 			},
// 			developersKeys: {
// 				path: CREDENTIALS,
// 				element: (
// 					<Credential loading={credentialsLoading} loaded={credentialsLoaded} />
// 				),
// 			},
// 			userRoles: {
// 				path: USER_ROLES,
// 				element: <UserRoles />,
// 			},
// 			subAccount: {
// 				path: SUB_ACCOUNT,
// 				element: <SubAccount />,
// 			},
// 			embedWeb: {
// 				path: INTEGRATE,
// 				element: <Integrate />,
// 			},
// 			apiDocs: {
// 				path: API,
// 				element: <Document />,
// 			},
// 			userAnalytics: {
// 				path: ANALYTICS,
// 				element: <Analytics />,
// 			},
// 			supportAgent: {
// 				path: SUPPORT_AGENT,
// 				element: <SupportAgent />,
// 			},
// 			billing: {
// 				path: BILLING,
// 				element: <Billing />,
// 			},
// 			checkDefense: {
// 				path: CHEQUE_FRAUD,
// 				element: <ChequeFraud />,
// 			},
// 		};
// 		return ALL_ROUTES_MODULE;
// 	}, [credentialsLoaded, credentialsLoading, fundsLoaded]);

// 	return { ALL_ROUTES_MODULE };
// };
